// SPDX-License-Identifier: AGPL-3.0-only

import type { DemGrid, GeographicBounds, GridMetrics, InflowSource } from './types';

const METERS_PER_DEGREE_LATITUDE = 111_132;

export function gridMetrics(grid: Pick<DemGrid, 'width' | 'height' | 'bounds'>): GridMetrics {
  const [west, south, east, north] = grid.bounds;
  const centerLatitudeRadians = ((south + north) * 0.5 * Math.PI) / 180;
  const metersPerDegreeLongitude = 111_320 * Math.cos(centerLatitudeRadians);
  const cellWidthMeters = ((east - west) * metersPerDegreeLongitude) / (grid.width - 1);
  const cellHeightMeters = ((north - south) * METERS_PER_DEGREE_LATITUDE) / (grid.height - 1);
  return {
    cellWidthMeters,
    cellHeightMeters,
    cellAreaSquareMeters: cellWidthMeters * cellHeightMeters
  };
}

export function longitudeLatitudeToGrid(
  bounds: GeographicBounds,
  width: number,
  height: number,
  longitude: number,
  latitude: number
): readonly [x: number, y: number] {
  const [west, south, east, north] = bounds;
  return [
    ((longitude - west) / (east - west)) * (width - 1),
    ((north - latitude) / (north - south)) * (height - 1)
  ];
}

export function gridToLongitudeLatitude(
  bounds: GeographicBounds,
  width: number,
  height: number,
  x: number,
  y: number
): readonly [longitude: number, latitude: number] {
  const [west, south, east, north] = bounds;
  return [west + (x / (width - 1)) * (east - west), north - (y / (height - 1)) * (north - south)];
}

export function buildSourceRateGrid(grid: DemGrid, sources: readonly InflowSource[]): Float32Array {
  const result = new Float32Array(grid.width * grid.height);
  const metrics = gridMetrics(grid);

  for (const source of sources) {
    if (source.flowRateCubicMetersPerSecond <= 0 || source.radiusMeters <= 0) continue;
    const [centerX, centerY] = longitudeLatitudeToGrid(
      grid.bounds,
      grid.width,
      grid.height,
      source.longitude,
      source.latitude
    );
    const radiusX = Math.max(1, source.radiusMeters / metrics.cellWidthMeters);
    const radiusY = Math.max(1, source.radiusMeters / metrics.cellHeightMeters);
    const minX = Math.max(0, Math.floor(centerX - radiusX));
    const maxX = Math.min(grid.width - 1, Math.ceil(centerX + radiusX));
    const minY = Math.max(0, Math.floor(centerY - radiusY));
    const maxY = Math.min(grid.height - 1, Math.ceil(centerY + radiusY));
    const weights: Array<readonly [index: number, weight: number]> = [];
    let weightSum = 0;

    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        const normalizedDistanceSquared =
          ((x - centerX) / radiusX) ** 2 + ((y - centerY) / radiusY) ** 2;
        if (normalizedDistanceSquared > 1) continue;
        const radial = 1 - Math.sqrt(normalizedDistanceSquared);
        const weight = radial * radial * (3 - 2 * radial);
        weights.push([y * grid.width + x, weight]);
        weightSum += weight;
      }
    }

    if (weightSum === 0) continue;
    for (const [index, weight] of weights) {
      result[index] =
        (result[index] ?? 0) +
        (source.flowRateCubicMetersPerSecond * weight) / weightSum / metrics.cellAreaSquareMeters;
    }
  }
  return result;
}
